/**
 * @file boostService.js
 * @description Boost credit and case promotion service. Manages the boost product
 *   catalog, credit pack pricing, balance queries, transaction history, and the
 *   atomic spend_credits RPC for activating promotions.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/boostService
 */
import { supabase } from "./supabase";
import { SPEND_CAP_ERROR, spendCapInfoFrom } from "./spendCap";
import { logCaughtError } from "./errorService";

// ── Product catalog ───────────────────────────────────────────────────────────
// credits: number of credits required
// expires: number of days until promotion expires (null = no expiry)
export const BOOST_PRODUCTS = {
  urgency_badge: {
    key:         "urgency_badge",
    label:       "Urgency Badge",
    credits:     2,
    description: "Adds an 'Immediate start' badge to this listing",
    expires:     null,
  },
  candidate_expand: {
    key:         "candidate_expand",
    label:       "Expand Radius",
    credits:     2,
    description: "Widens provider match radius by +10 miles for 14 days",
    expires:     14,
  },
  reach_blast: {
    key:         "reach_blast",
    label:       "Reach Blast",
    credits:     1,           // PER RECIPIENT — total cost is recipients × this rate
    perRecipient: true,
    description: "Pushes a message to every matched-and-uncontacted provider in radius. Cost = recipients × rate.",
    expires:     null,
  },
  direct_outreach: {
    key:         "direct_outreach",
    label:       "Direct Outreach",
    credits:     1,           // PER RECIPIENT
    perRecipient: true,
    proOnly:     true,
    description: "Send a curated message to specific providers you pick from the Cases Map overlay. Pro only. Cost = recipients × rate.",
    expires:     null,
  },
  spotlight: {
    key:         "spotlight",
    label:       "Spotlight",
    credits:     4,
    description: "Pins to top of search results for 7 days",
    expires:     7,
  },
  bundle: {
    key:         "bundle",
    label:       "Fast Fill Bundle",
    credits:     5,
    description: "Spotlight + Urgency Badge for less, plus the Reach Blast ranking boost",
    expires:     7,
  },
  // Agency-direct-invite — paid as of platform_config.boost_agency_invite_credits.
  // Charged once per (agency, therapist, case). Duplicate live invites
  // (pending/accepted) block before charging; previously-declined invites
  // are re-chargeable so agencies can re-engage.
  agency_invite: {
    key:         "agency_invite",
    label:       "Agency Invite",
    credits:     3,
    description: "Direct invite to a specific provider, pinned to the top of their Best Matches and they get notified.",
    expires:     null,
  },
};

/**
 * Returns BOOST_PRODUCTS with credit costs overridden by admin config.
 * Accepts a Map (from loadAllConfigs) or plain object (from getCachedConfig calls).
 * Falls back to hardcoded defaults when config is null/undefined.
 */
export function getBoostProducts(config) {
  const get = (key, fallback) => {
    if (!config) return fallback;
    if (config instanceof Map) return config.get(key) ?? fallback;
    return config[key] ?? fallback;
  };
  return {
    urgency_badge:    { ...BOOST_PRODUCTS.urgency_badge,    credits: get("boost_urgency_badge_credits",    2) },
    candidate_expand: { ...BOOST_PRODUCTS.candidate_expand, credits: get("boost_candidate_expand_credits", 2) },
    // reach_blast is per-recipient now — `credits` here is the per-recipient
    // rate, total cost is recipients × rate (computed at send-time on the
    // server). Old config key (boost_reach_blast_credits) is ignored.
    reach_blast:      { ...BOOST_PRODUCTS.reach_blast,      credits: get("reach_blast_credits_per_recipient", 1) },
    direct_outreach:  { ...BOOST_PRODUCTS.direct_outreach,  credits: get("direct_outreach_credits_per_recipient", 1) },
    spotlight:        { ...BOOST_PRODUCTS.spotlight,        credits: get("boost_spotlight_credits",        4) },
    bundle:           { ...BOOST_PRODUCTS.bundle,           credits: get("boost_bundle_credits",           5) },
    agency_invite:    { ...BOOST_PRODUCTS.agency_invite,    credits: get("boost_agency_invite_credits",    3) },
    // Cross-state premium for inviting a therapist whose home state differs from
    // the case state (remote/sponsorship placement). Server charges this in
    // send_paid_agency_invite when the invite is cross-state.
    agency_invite_cross_state: { ...BOOST_PRODUCTS.agency_invite, key: "agency_invite_cross_state", credits: get("boost_agency_invite_cross_state_credits", 8) },
  };
}

// Credit pack defaults — also stored in configService so admin can override.
// Use getCreditPacks(config) in UI components for the live, configurable prices.
export const CREDIT_PACKS = [
  { credits: 1,  price: 15,  label: "1 credit"   },
  { credits: 5,  price: 65,  label: "5 credits"  },
  { credits: 20, price: 220, label: "20 credits" },
  { credits: 50, price: 499, label: "50 credits" },
];

/** Returns CREDIT_PACKS with prices overridden by admin config when available. */
export function getCreditPacks(config) {
  if (!config) return CREDIT_PACKS;
  // Accepts the Map from loadAllConfigs as well as a plain object, like getBoostProducts.
  if (config instanceof Map) config = Object.fromEntries(config);
  return [
    { credits: 1,  price: config.credit_pack_1_price  ?? 15,  label: "1 credit"   },
    { credits: 5,  price: config.credit_pack_5_price  ?? 65,  label: "5 credits"  },
    { credits: 20, price: config.credit_pack_20_price ?? 220, label: "20 credits" },
    { credits: 50, price: config.credit_pack_50_price ?? 499, label: "50 credits" },
  ];
}

// ── Get credit balance ────────────────────────────────────────────────────────
export async function getBalance(agencyId) {
  if (!agencyId) return 0;
  const { data, error } = await supabase
    .from("boost_credits")
    .select("balance")
    .eq("agency_id", agencyId)
    .maybeSingle();
  if (error || !data) return 0;
  return data.balance;
}

// ── Get transaction history ───────────────────────────────────────────────────
export async function getTransactions(agencyId, limit = 20) {
  if (!agencyId) return [];
  const { data, error } = await supabase
    .from("boost_credit_transactions")
    .select("*")
    .eq("agency_id", agencyId)
    .order("created_at", { ascending: false })
    .limit(limit);
  return error ? [] : (data || []);
}

// ── Get case IDs that are SPONSORED — i.e. carry a top-placement product ──────
// Only spotlight (and bundle, which includes spotlight) earn the "Sponsored"
// label. Previously this returned ANY active promotion, so a 2-credit urgency
// badge got the 3-credit Spotlight's "Sponsored" treatment for free (monetization
// hole). Placement itself is now driven by the weighted rank_score, not this set;
// this drives the label only.
export async function getSpotlightedCaseIds() {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("case_promotions")
    .select("case_id")
    .eq("status", "active")
    .in("type", ["spotlight", "bundle"])
    .or(`expires_at.is.null,expires_at.gt.${now}`)
    .limit(1000); // egress cap — already minimal (case_id only), but bound the scan
  return error ? new Set() : new Set((data || []).map(r => r.case_id));
}

// ── Get active promotions for a single case ───────────────────────────────────
export async function getCasePromotions(caseId) {
  if (!caseId) return [];
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("case_promotions")
    .select("*")
    .eq("case_id", caseId)
    .eq("status", "active")
    .or(`expires_at.is.null,expires_at.gt.${now}`);
  return error ? [] : (data || []);
}

// ── Get all active promotions across an agency's cases ───────────────────────
export async function getAgencyPromotions(agencyId) {
  if (!agencyId) return [];
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("case_promotions")
    .select("*")
    .eq("agency_id", agencyId)
    .eq("status", "active")
    .or(`expires_at.is.null,expires_at.gt.${now}`);
  return error ? [] : (data || []);
}

// ── Spend credits and activate a promotion ────────────────────────────────────
// Uses the atomic spend_credits SQL RPC to eliminate the TOCTOU race condition.
// Returns { ok: true, balance: N } or { ok: false, error: string }
export async function spendCredits(agencyId, { caseId, productKey, boostProducts }) {
  const product = (boostProducts || BOOST_PRODUCTS)[productKey];
  if (!product) return { ok: false, error: "Unknown product" };

  // Require a finite, positive number of days — a malformed product.expires
  // would otherwise produce new Date(NaN).toISOString(), which THROWS
  // ("Invalid time value") and aborts the whole spend. 86_400_000 = ms/day.
  const expiresAt = (Number.isFinite(product.expires) && product.expires > 0)
    ? new Date(Date.now() + product.expires * 86_400_000).toISOString()
    : null;

  // Single atomic RPC: deducts balance, logs transaction, records promotion.
  // Raises 'insufficient_credits' if balance < required — no race condition possible.
  const { data: newBalance, error } = await supabase.rpc("spend_credits", {
    p_agency_id:   agencyId,
    p_product_key: productKey,
    p_case_id:     caseId || null,
    p_credits:     product.credits,
    p_label:       product.label,
    p_expires_at:  expiresAt,
  });

  if (error) {
    if (error.message?.includes("insufficient_credits")) {
      return { ok: false, error: "insufficient_credits" };
    }
    if (error.message?.includes("spotlight_area_full")) {
      const match = error.message.match(/spotlight_area_full: (.+)/);
      return { ok: false, error: "spotlight_area_full", detail: match ? match[1] : "No spotlight slots available in this area" };
    }
    // Surface the raw Postgres/PostgREST message in dev; keep it short in prod.
    const detail = error.message || "unknown error";
    console.warn("[boostService] spendCredits failed:", detail);
    // insufficient_credits and spotlight_area_full returned above are explained outcomes; this is not.
    logCaughtError(error, "boost_spend_failed", { kind: productKey, listingId: caseId, step: "rpc" });
    return { ok: false, error: "spend_failed", detail };
  }

  return { ok: true, balance: newBalance };
}

// ── Check spotlight area cap for a case ──────────────────────────────────────
// Calls the check_spotlight_area_cap RPC to determine if a new spotlight can be
// applied in the case's geographic area. Fails open — returns available: true on
// any RPC error so the UI is never blocked by a transient DB failure.
export async function checkSpotlightAreaCap(caseId) {
  if (!caseId) return { available: true, unlimited: true };
  const { data, error } = await supabase.rpc("check_spotlight_area_cap", {
    p_case_id: caseId,
  });
  if (error) {
    console.warn("[boostService] checkSpotlightAreaCap failed, failing open:", error.message);
    return { available: true, unlimited: true };
  }
  // data is the JSONB result: { available, used, limit, slots_left, area, unlimited }
  return data;
}

// ── Add credits — service-role / webhook path ─────────────────────────────────
// Only safe for service-role callers (Stripe webhook, seed scripts).
// Admin UI must use adminGiftCredits() below — that path checks role server-side.
export async function addCredits(agencyId, amount, description = "Purchase") {
  if (!agencyId || amount <= 0) return { ok: false, error: "Invalid input" };

  const { data: newBalance, error } = await supabase.rpc("add_credits", {
    p_agency_id:   agencyId,
    p_amount:      amount,
    p_description: description,
  });

  if (error) {
    logCaughtError(error, "credits_add_failed", { step: "rpc", credits: amount });
    return { ok: false, error: "Failed to add credits" };
  }
  return { ok: true, balance: newBalance };
}

// ── Admin gift credits — admin UI path ────────────────────────────────────────
// Calls the admin_gift_credits RPC which verifies the caller is an admin
// server-side before granting. Never call addCredits() from admin UI — that
// RPC is service_role only and will fail silently for authenticated callers.
export async function adminGiftCredits(agencyId, amount, description = "Admin gift") {
  if (!agencyId || amount <= 0) return { ok: false, error: "Invalid input" };

  const { data: newBalance, error } = await supabase.rpc("admin_gift_credits", {
    p_agency_id:   agencyId,
    p_amount:      amount,
    p_description: description,
  });

  if (error) {
    if (error.message?.includes("forbidden")) return { ok: false, error: "Admin role required" };
    if (error.message?.includes("invalid_amount")) return { ok: false, error: "Amount must be 1–10,000" };
    return { ok: false, error: error.message || "Failed to gift credits" };
  }
  return { ok: true, balance: newBalance };
}

/**
 * @description Agency-facing credit pack purchase. Single atomic RPC that:
 *   1. Validates pack size against platform_config (refuses unknown sizes)
 *   2. Reads the price server-side (client cannot tamper with the amount)
 *   3. Increments the balance + writes the audit ledger entry
 *   4. Creates the invoice with proper line item
 *   All in one transaction — if any step fails, the whole purchase rolls
 *   back. Mirrors purchaseExtraSlot in caseService.js.
 *
 *   When Stripe goes live, the webhook flips `status: paid → open` (or
 *   creates as `open` and flips on charge confirm) without touching this
 *   function — same RPC continues to work.
 *
 * @param {string} agencyId
 * @param {number} packCredits - Pack size (must match a credit_pack_<N>_price config)
 * @returns {Promise<{ok:boolean, balance?:number, credits_added?:number, amount_cents?:number, invoice_id?:string, invoice_number?:string, error?:string}>}
 */
export async function purchaseCreditPack(agencyId, packCredits) {
  if (!agencyId) return { ok: false, error: "agency_id required" };
  if (!packCredits || packCredits <= 0) return { ok: false, error: "Invalid pack size" };

  const { data, error } = await supabase.rpc("purchase_credit_pack", {
    p_agency_id:    agencyId,
    p_pack_credits: packCredits,
  });

  if (error) {
    // Spend cap (Billing, Cost Control): the invoice trigger rejected the purchase
    // and the whole RPC rolled back, so no credits were granted.
    const spendCap = spendCapInfoFrom(error);
    if (spendCap) return { ok: false, error: SPEND_CAP_ERROR, spendCap };
    // eslint-disable-next-line no-console
    console.warn("[boostService] purchaseCreditPack failed:", error);
    logCaughtError(error, "credit_pack_purchase_failed", { step: "rpc", credits: packCredits });
    return { ok: false, error: error.message || "Could not purchase credit pack" };
  }
  return data || { ok: false, error: "no_response" };
}

// ── Format a transaction amount for display ───────────────────────────────────
export function fmtTx(tx) {
  const sign   = tx.amount > 0 ? "+" : "";
  const label  = tx.promotion_type
    ? BOOST_PRODUCTS[tx.promotion_type]?.label || tx.promotion_type
    : tx.description || tx.reason;
  return { sign, label, amount: `${sign}${tx.amount} cr` };
}
